import {inject} from '@angular/core';
import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {catchError, EMPTY, throwError, timeout} from 'rxjs';
import {DialogErrorService} from "../../shared/components/dialog-error/dialog-error.service";
import * as Sentry from "@sentry/angular";
import {DialogErrorData} from "../../shared/components/dialog-error/models/dialog-error";
import {UiUtils} from "../../shared/utils/ui.utils";


export const HttpErrorInterceptor: HttpInterceptorFn = (request, next) => {
    const dialogService = inject(DialogErrorService);

    const WHITELISTED_URLS = [
      /^https:\/\/(carool-vin-(?:dev|val|prod))\.s3\.eu-west-1\.amazonaws\.com\/analyses\/.*$/,
      /^https:\/\/carool-configuration-(?:dev|val|prod).s3.eu-west-1.amazonaws.com\/.*$/,
      /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/external\/v1\/web-app\/analyses\/\d+\/(?:tread|sidewall)-pictures$/,
      /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/external\/v1\/web-app\/analyses\/\d+\/(?:wear|reference)-cbd$/,
      /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/external\/v1\/web-app\/analyses$/,
      /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/external\/v1\/countries\/.*\/settings$/,
      /^assets\/.*$/
    ];
    const TIMEOUT_URLS = [
      {
        url: /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/external\/v1\/web-app\/analyses\/\d+\/(?:tread|sidewall)-pictures$/,
        timeout: 60000
      },
      {
        url: /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com\/api\/external\/v1\/web-app\/analyses\/by-picture$/,
        timeout: 30000
      },
      {
        url: /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com(?::\d+)?\/api\/reference\/vehicles\/(.*)\/info$/,
        timeout: 30000
      },
      {
        url: /^https:\/\/(?:dev\.rolling\.|val\.rolling\.|)ca-rool\.com\/api\/.*$/,
        timeout: 10000
      }
    ];
    const interceptedHttpCodes = [0, 400, 401, 403, 409, 500];

    const to = TIMEOUT_URLS.find((value) => request.url.match(value.url))?.timeout;
    const isWhitelistedUrl = WHITELISTED_URLS.some((regex) => regex.test(request.url));
    let request$ = next(request);

    // If the request has timeout management then do it.
    if (to) {
      request$ = request$.pipe(timeout(to),
        catchError((response: Error) => {
          if (response.name === 'TimeoutError') {
            Sentry.captureMessage('Timeout no network', {extra: {response}});
            if (isWhitelistedUrl) {
              return throwError(() => response);
            }
            dialogService.show(UiUtils.getNoNetworkDialog());
            return EMPTY;
          } else {
            return throwError(() => response as HttpErrorResponse);
          }
        }))
    }

    return request$.pipe(catchError((response: HttpErrorResponse) => {
      if (!isWhitelistedUrl && interceptedHttpCodes.includes(response.status) && response.url?.startsWith('http')) {
        let data = new DialogErrorData();
        switch (response.status) {
          case 0:
            data = UiUtils.getNoNetworkDialog();
            Sentry.captureMessage('Http Network Error', {extra: {response, request}});
            break;
          case 400:
          case 401:
          case 403:
          case 409:
            Sentry.captureException(new Error(`Http ${response.status} Error`), {extra: {response, request}});
            return throwError(() => response);
          case 500:
            data.title = 'error.unknown.title';
            Sentry.captureException(new Error('Http Backend Error'), {extra: {response, request}});
            break;
        }
        if (data.title) dialogService.show(data);
        return EMPTY;
      }
      return throwError(() => response);
    }));
}
